import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LanguageContext';
import { Users, Plus, RefreshCw, Settings, Edit2, Trash2, ChevronLeft, Power, AlertTriangle } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import CreateFunctionalGroupWizard from '../../components/admin/CreateFunctionalGroupWizard';
import ManageGroupModal from '../../components/admin/ManageGroupModal';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Functional Groups Management Page (Enhanced)
 * EMZI CI Aligned - Minimalist Icons, Blue Gradient, Clean Design
 * Features: Edit Group Name, Delete Group, Mobile Optimized
 */

const FunctionalGroupsPage = () => {
  const { user, isAdmin } = useAuth();
  const t = useLang();
  const navigate = useNavigate();
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteGroupData, setDeleteGroupData] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');

  // Check admin access
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
    } else {
      fetchGroups();
    }
  }, [isAdmin, navigate]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroupName = (group) => {
    setEditingGroupId(group.group_id);
    setEditedName(group.name);
  };

  const handleSaveGroupName = async (groupId) => {
    if (!editedName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${groupId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: editedName })
        }
      );

      if (response.ok) {
        toast.success('Group name updated successfully');
        setEditingGroupId(null);
        fetchGroups();
      } else {
        toast.error('Failed to update group name');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group name');
    }
  };

  const handleDeactivateGroup = async (group) => {
    if (!window.confirm(`⚠️ DEACTIVATE GROUP\n\nAre you sure you want to deactivate "${group.name}"?\n\nThe group will become inactive but can be reactivated later.`)) {
      return;
    }

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${group.group_id}/deactivate`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success(`Group "${group.name}" deactivated successfully`);
        fetchGroups();
      } else {
        toast.error('Failed to deactivate group');
      }
    } catch (error) {
      console.error('Error deactivating group:', error);
      toast.error('Failed to deactivate group');
    }
  };

  const handleOpenDeleteModal = (group) => {
    setDeleteGroupData(group);
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  const handlePermanentDelete = async () => {
    if (deletePassword !== 'emzi4111') {
      toast.error('Invalid password');
      return;
    }

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups/${deleteGroupData.group_id}/permanent`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: deletePassword })
        }
      );

      if (response.ok) {
        toast.success(`Group "${deleteGroupData.name}" permanently deleted`);
        setShowDeleteModal(false);
        setDeleteGroupData(null);
        setDeletePassword('');
        fetchGroups();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete group');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleManageMembers = (group) => {
    setSelectedGroup(group);
    setShowManageModal(true);
  };

  if (!isAdmin()) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: EMZI_COLORS.background,
      paddingBottom: '80px'
    }}>
      {/* Header - EMZI CI Style */}
      <div style={{
        background: 'linear-gradient(135deg, #0055FF 0%, #0044CC 100%)',
        padding: '20px',
        color: 'white',
        boxShadow: EMZI_SHADOWS.lg
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Users size={28} strokeWidth={2} />
              {t('admin.functionalGroups.title')}
            </h1>
            <p style={{
              fontSize: '14px',
              opacity: 0.9,
              margin: '4px 0 0 0'
            }}>
              {t('admin.functionalGroups.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px',
        overflowX: 'auto'
      }}>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            {t('admin.functionalGroups.totalGroups')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: EMZI_COLORS.primary
          }}>
            {loading ? '...' : groups.length}
          </div>
        </div>
        <div style={{
          flex: '1',
          minWidth: '140px',
          background: 'white',
          padding: '16px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.sm
        }}>
          <div style={{
            fontSize: '12px',
            color: EMZI_COLORS.textSecondary,
            marginBottom: '4px'
          }}>
            {t('admin.functionalGroups.activeGroups')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#10b981'
          }}>
            {loading ? '...' : groups.filter(g => g.is_active).length}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        padding: '0 20px 16px',
        display: 'flex',
        gap: '12px'
      }}>
        <button
          onClick={() => setShowWizard(true)}
          style={{
            flex: '1',
            padding: '12px 20px',
            background: EMZI_COLORS.primary,
            color: 'white',
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
            boxShadow: EMZI_SHADOWS.sm,
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = EMZI_SHADOWS.md;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
          }}
        >
          <Plus size={18} strokeWidth={2} />
          {t('admin.functionalGroups.newGroup')}
        </button>
        <button
          onClick={fetchGroups}
          disabled={loading}
          style={{
            padding: '12px 20px',
            background: 'white',
            color: EMZI_COLORS.textPrimary,
            border: `2px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '48px',
            transition: 'transform 0.2s',
            opacity: loading ? 0.6 : 1
          }}
          onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'scale(1.02)')}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <RefreshCw size={18} strokeWidth={2} />
        </button>
      </div>

      {/* Groups List */}
      <div style={{ padding: '0 20px 20px' }}>
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: EMZI_COLORS.textSecondary
          }}>
            Loading groups...
          </div>
        ) : groups.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            boxShadow: EMZI_SHADOWS.sm
          }}>
            <Users size={48} color={EMZI_COLORS.textSecondary} style={{ marginBottom: '16px' }} />
            <p style={{ color: EMZI_COLORS.textSecondary, margin: 0 }}>
              {t('admin.functionalGroups.noGroups')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {groups.map((group) => (
              <div
                key={group.group_id}
                style={{
                  background: 'white',
                  borderRadius: EMZI_RADIUS.lg,
                  padding: '16px',
                  boxShadow: EMZI_SHADOWS.sm,
                  border: `1px solid ${EMZI_COLORS.border}`,
                  transition: 'box-shadow 0.2s, transform 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = EMZI_SHADOWS.md;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Group Header with Edit/Delete */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ flex: 1 }}>
                    {editingGroupId === group.group_id ? (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: `2px solid ${EMZI_COLORS.primary}`,
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '14px',
                            fontWeight: '600',
                            outline: 'none'
                          }}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveGroupName(group.group_id);
                          }}
                        />
                        <button
                          onClick={() => handleSaveGroupName(group.group_id)}
                          style={{
                            padding: '8px 12px',
                            background: EMZI_COLORS.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingGroupId(null)}
                          style={{
                            padding: '8px 12px',
                            background: EMZI_COLORS.surface,
                            color: EMZI_COLORS.textSecondary,
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: EMZI_COLORS.textPrimary,
                          marginBottom: '4px'
                        }}>
                          {group.name}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          color: EMZI_COLORS.textSecondary
                        }}>
                          <span style={{
                            padding: '2px 8px',
                            background: `${EMZI_COLORS.accent}20`,
                            color: EMZI_COLORS.accent,
                            borderRadius: EMZI_RADIUS.sm,
                            fontWeight: '600'
                          }}>
                            {group.purpose_tag}
                          </span>
                          <span>•</span>
                          <span>{group.member_count} members</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {editingGroupId !== group.group_id && (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleEditGroupName(group)}
                        style={{
                          padding: '6px',
                          background: EMZI_COLORS.surface,
                          border: 'none',
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e0e7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = EMZI_COLORS.surface}
                      >
                        <Edit2 size={16} color={EMZI_COLORS.primary} />
                      </button>
                      <button
                        onClick={() => handleDeactivateGroup(group)}
                        style={{
                          padding: '6px',
                          background: '#fef3c7',
                          border: 'none',
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fde68a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fef3c7'}
                        title="Deactivate Group"
                      >
                        <Power size={16} color="#f59e0b" />
                      </button>
                      <button
                        onClick={() => handleOpenDeleteModal(group)}
                        style={{
                          padding: '6px',
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: EMZI_RADIUS.sm,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}
                        title="Permanently Delete"
                      >
                        <Trash2 size={16} color="#dc2626" />
                      </button>
                      <span style={{
                        fontSize: '20px',
                        color: group.is_active ? '#10b981' : '#CCCCCC',
                        marginLeft: '4px'
                      }}>
                        ●
                      </span>
                    </div>
                  )}
                </div>

                {/* Manage Members Button */}
                {editingGroupId !== group.group_id && (
                  <button
                    onClick={() => handleManageMembers(group)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: `${EMZI_COLORS.primary}10`,
                      color: EMZI_COLORS.primary,
                      border: `1px solid ${EMZI_COLORS.primary}30`,
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = `${EMZI_COLORS.primary}20`}
                    onMouseLeave={(e) => e.currentTarget.style.background = `${EMZI_COLORS.primary}10`}
                  >
                    <Settings size={16} strokeWidth={2} />
                    {t('admin.members.manageMembers')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <CreateFunctionalGroupWizard
          onClose={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            fetchGroups();
          }}
        />
      )}

      {/* Manage Members Modal */}
      {showManageModal && selectedGroup && (
        <ManageGroupModal
          group={selectedGroup}
          onClose={() => {
            setShowManageModal(false);
            setSelectedGroup(null);
          }}
          onUpdate={fetchGroups}
        />
      )}

      {/* Permanent Delete Modal with Password */}
      {showDeleteModal && deleteGroupData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: EMZI_RADIUS.lg,
            maxWidth: '400px',
            width: '100%',
            boxShadow: EMZI_SHADOWS.xl
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${EMZI_COLORS.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle size={24} color="#dc2626" />
              </div>
              <div>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: EMZI_COLORS.textPrimary,
                  margin: 0
                }}>
                  Permanently delete this group?
                </h2>
                <p style={{
                  fontSize: '12px',
                  color: EMZI_COLORS.textSecondary,
                  margin: '4px 0 0 0'
                }}>
                  This action cannot be undone
                </p>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{
                padding: '12px',
                background: '#fef3c7',
                borderRadius: EMZI_RADIUS.md,
                marginBottom: '20px',
                border: '1px solid #fbbf24'
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#92400e',
                  marginBottom: '4px'
                }}>
                  Group: {deleteGroupData.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#78350f'
                }}>
                  All member assignments will be permanently removed
                </div>
              </div>

              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: EMZI_COLORS.textPrimary,
                marginBottom: '8px'
              }}>
                Enter passphrase to confirm:
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="emzi4111"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `2px solid ${deletePassword === 'emzi4111' ? '#10b981' : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && deletePassword === 'emzi4111') {
                    handlePermanentDelete();
                  }
                }}
              />
              <div style={{
                fontSize: '11px',
                color: EMZI_COLORS.textSecondary,
                marginTop: '6px',
                fontStyle: 'italic'
              }}>
                Type the exact passphrase to enable the Confirm button
              </div>
            </div>

            {/* Actions */}
            <div style={{
              padding: '0 24px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteGroupData(null);
                  setDeletePassword('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: EMZI_COLORS.textPrimary,
                  border: `2px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePermanentDelete}
                disabled={deletePassword !== 'emzi4111'}
                style={{
                  padding: '10px 20px',
                  background: deletePassword === 'emzi4111' ? '#dc2626' : '#e5e7eb',
                  color: deletePassword === 'emzi4111' ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: deletePassword === 'emzi4111' ? 'pointer' : 'not-allowed',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Trash2 size={18} />
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunctionalGroupsPage;
